import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import { useIsAdmin } from '@/lib/auth'
import { toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import {
  useDeleteCategory,
  useDeleteProject,
  useSaveCategory,
  useSaveProject,
} from '@/lib/clients-api'
import { usePersons } from '@/lib/settings-api'
import type {
  CategoryInput,
  CategoryView,
  ClientDetail,
  ProjectInput,
  ProjectView,
} from '@/lib/clients-types'
import { cn } from '@/lib/utils'

function projectRatePeriod(client: ClientDetail, projectId: string) {
  return client.ratePeriods.find((rate) => rate.projectId === projectId && rate.validTo === null) ?? null
}

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

export function ProjectsCategoriesCard({ client }: { client: ClientDetail }) {
  const isAdmin = useIsAdmin()
  const [projectEditor, setProjectEditor] = useState<{ project?: ProjectView } | null>(null)
  const [categoryEditor, setCategoryEditor] = useState<{ projectId: string; category?: CategoryView } | null>(null)

  return (
    <Card>
      <CardHeader
        title="Projecten en categorieen"
        action={
          isAdmin ? (
            <button type="button" className="btn-ghost h-8 px-2 text-sm" onClick={() => setProjectEditor({})}>
              <Plus className="h-4 w-4" /> Project
            </button>
          ) : null
        }
      />
      <div className="mt-4 space-y-3">
        {client.projects.length === 0 ? (
          <p className="text-sm text-ink-faint">Nog geen projecten.</p>
        ) : (
          client.projects.map((project) => {
            const categories = client.categories.filter((c) => c.projectId === project.id)
            const rate = projectRatePeriod(client, project.id)
            return (
              <div key={project.id} className={cn('rounded-lg border border-hairline', !project.active && 'opacity-60')}>
                <button
                  type="button"
                  onClick={() => setProjectEditor({ project })}
                  className="block w-full rounded-t-lg px-3 py-2 text-left transition-colors hover:bg-fill-1"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-ink">
                      {project.name}
                      {project.code ? <span className="ml-1.5 text-xs text-ink-faint">{project.code}</span> : null}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {project.fixedFee ? <span className="rounded-pill bg-fill-2 px-2 py-0.5 text-xs text-ink-soft">fixed fee</span> : null}
                      {project.categoryRequired ? <span className="rounded-pill bg-status-approval/10 px-2 py-0.5 text-xs text-status-approval">categorie vereist</span> : null}
                      {rate ? (
                        <span className="rounded-pill bg-fill-2 px-2 py-0.5 text-xs text-ink-soft" title="Projecttarief">
                          <span className="money">{formatEuros(toNumber(rate.hourlyRate))}/u</span>
                          {rate.validFrom > todayIso() ? <span className="ml-1 text-ink-faint">vanaf {rate.validFrom}</span> : null}
                        </span>
                      ) : null}
                    </span>
                  </div>
                </button>
                <div className="border-t border-hairline px-3 py-2">
                  <div className="mb-1.5 flex items-center justify-between">
                    <p className="label-caps text-ink-faint">Categorieen</p>
                    {isAdmin ? (
                      <button type="button" className="btn-ghost h-6 px-1.5 text-xs" onClick={() => setCategoryEditor({ projectId: project.id })}>
                        <Plus className="h-3 w-3" /> Categorie
                      </button>
                    ) : null}
                  </div>
                  {categories.length === 0 ? (
                    <p className="text-xs text-ink-faint">Geen categorieen.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {categories.map((category) => (
                        <button
                          type="button"
                          key={category.id}
                          onClick={() => setCategoryEditor({ projectId: project.id, category })}
                          disabled={!isAdmin}
                          className={cn('rounded-pill bg-fill-2 px-2.5 py-1 text-xs text-ink-soft transition-colors hover:bg-fill-1 disabled:cursor-default disabled:hover:bg-fill-2', !category.active && 'opacity-60')}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
      {projectEditor ? <ProjectEditorModal client={client} project={projectEditor.project} onClose={() => setProjectEditor(null)} /> : null}
      {categoryEditor ? (
        <CategoryEditorModal client={client} projectId={categoryEditor.projectId} category={categoryEditor.category} onClose={() => setCategoryEditor(null)} />
      ) : null}
    </Card>
  )
}

const NO_LEAD = 'none'

function ProjectEditorModal({ client, project, onClose }: { client: ClientDetail; project?: ProjectView; onClose: () => void }) {
  const canEdit = useIsAdmin()
  // Alleen wie in de vooruitblik voorkomt: actief en geen externe boeker.
  const leadOptions = (usePersons().data ?? [])
    .filter((person) => person.active && person.role !== 'EXTERNAL')
    .map((person) => ({ value: person.id, label: person.displayName }))
  const saveProject = useSaveProject(client.id)
  const deleteProject = useDeleteProject(client.id)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [name, setName] = useState(project?.name ?? '')
  const [code, setCode] = useState(project?.code ?? '')
  const [categoryRequired, setCategoryRequired] = useState(project?.categoryRequired ?? false)
  const [active, setActive] = useState(project?.active ?? true)
  const [fixedFee, setFixedFee] = useState(project?.fixedFee ?? false)
  const [attn, setAttn] = useState(project?.attn ?? '')
  const [invoiceEmail, setInvoiceEmail] = useState(project?.invoiceEmail ?? '')
  const [notes, setNotes] = useState(project?.notes ?? '')
  const [leadId, setLeadId] = useState(project?.lead?.id ?? NO_LEAD)
  const [copyFrom, setCopyFrom] = useState('none')
  const fieldClass = cn('field', !canEdit && 'bg-fill-1 text-ink-soft')
  const attnPlaceholder = canEdit ? (client.attn ? `${client.attn} (van klant)` : 'Leeg = klantwaarde') : client.attn ?? 'Geen'
  const invoiceEmailPlaceholder = canEdit ? (client.invoiceEmail ? `${client.invoiceEmail} (van klant)` : 'Leeg = klantwaarde') : client.invoiceEmail ?? 'Geen'

  function save() {
    if (!name.trim()) return
    const input: ProjectInput = {
      name: name.trim(),
      code: code.trim() || null,
      categoryRequired,
      active,
      fixedFee,
      attn: attn.trim() || null,
      invoiceEmail: invoiceEmail.trim() || null,
      notes: notes.trim() || null,
      leadId: leadId === NO_LEAD ? null : leadId,
    }
    if (!project && copyFrom !== 'none') {
      input.copyCategoriesFromProjectId = copyFrom
    }
    saveProject.mutate({ projectId: project?.id, input }, { onSuccess: onClose })
  }

  if (confirmDelete && project) {
    return (
      <Modal
        open
        onOpenChange={(o) => !o && setConfirmDelete(false)}
        title="Project verwijderen"
        description="Dit project wordt definitief verwijderd. Dit kan alleen als er nog geen uren, reiskosten, facturen, rapporten, categorieen of een tarief aan hangen."
        footer={
          <>
            <button type="button" className="btn-ghost" onClick={() => setConfirmDelete(false)}>Annuleren</button>
            <button
              type="button"
              className="btn-primary bg-status-overdue hover:bg-status-overdue/90"
              onClick={() => deleteProject.mutate(project.id, { onSuccess: onClose })}
              disabled={deleteProject.isPending}
            >
              <Trash2 className="h-4 w-4" />
              {deleteProject.isPending ? 'Verwijderen…' : 'Verwijder'}
            </button>
          </>
        }
      >
        <p className="text-sm text-ink-soft">Weet je het zeker?</p>
      </Modal>
    )
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={project ? (canEdit ? 'Project bewerken' : 'Project') : 'Nieuw project'}
      footer={
        canEdit ? (
          <>
            {project ? (
              <button
                type="button"
                className="btn-ghost mr-auto text-status-overdue hover:text-status-overdue"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4" /> Verwijder
              </button>
            ) : null}
            <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
            <button type="button" className="btn-primary" onClick={save} disabled={!name.trim() || saveProject.isPending}>
              {saveProject.isPending ? 'Opslaan…' : 'Opslaan'}
            </button>
          </>
        ) : (
          <button type="button" className="btn-secondary" onClick={onClose}>Sluiten</button>
        )
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Naam</span>
          <input value={name} onChange={(e) => setName(e.target.value)} readOnly={!canEdit} className={fieldClass} placeholder={canEdit ? 'Projectnaam' : ''} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Code</span>
          <input value={code} onChange={(e) => setCode(e.target.value)} readOnly={!canEdit} className={fieldClass} placeholder={canEdit ? 'Bijv. ZIG-04' : 'Geen code'} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">T.a.v. op de factuur</span>
          <input value={attn} onChange={(e) => setAttn(e.target.value)} readOnly={!canEdit} className={fieldClass} placeholder={attnPlaceholder} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Factuur-e-mail</span>
          <input value={invoiceEmail} onChange={(e) => setInvoiceEmail(e.target.value)} type="email" readOnly={!canEdit} className={fieldClass} placeholder={invoiceEmailPlaceholder} />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Opmerkingen</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} readOnly={!canEdit} className={cn(fieldClass, 'resize-none')} placeholder={canEdit ? 'Afspraken of achtergrond bij dit project' : 'Geen opmerkingen'} />
          <span className="mt-1 block text-xs text-ink-faint">Alleen zichtbaar in klantbeheer, niet op de factuur.</span>
        </label>
        {canEdit ? <p className="text-xs text-ink-faint">Rapport-ontvangers stel je in op de pagina Rapportages.</p> : null}
        <div className="space-y-2 rounded-lg bg-fill-1 px-3 py-2.5">
          <label className={cn('flex items-center gap-1.5 text-sm text-ink-soft', canEdit && 'cursor-pointer')}>
            <input type="checkbox" checked={categoryRequired} onChange={(e) => setCategoryRequired(e.target.checked)} disabled={!canEdit} className="h-3.5 w-3.5 accent-accent-600" />
            Categorie verplicht
          </label>
          <label className={cn('flex items-center gap-1.5 text-sm text-ink-soft', canEdit && 'cursor-pointer')}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={!canEdit} className="h-3.5 w-3.5 accent-accent-600" />
            Actief
          </label>
          <label className={cn('flex items-center gap-1.5 text-sm text-ink-soft', canEdit && 'cursor-pointer')}>
            <input type="checkbox" checked={fixedFee} onChange={(e) => setFixedFee(e.target.checked)} disabled={!canEdit} className="h-3.5 w-3.5 accent-accent-600" />
            Fixed fee (buiten automatische facturatie)
          </label>
        </div>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Kartrekker</span>
          <Select
            value={leadId}
            onValueChange={setLeadId}
            options={[{ value: NO_LEAD, label: 'Niemand aangewezen' }, ...leadOptions]}
            disabled={!canEdit}
          />
          <span className="mt-1 block text-xs text-ink-faint">
            Wie dit project trekt. Zichtbaar in de vooruitblik; geeft geen rechten en stuurt geen meldingen.
          </span>
        </label>
        {!project && client.projects.length > 0 ? (
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Categorieen kopieren van</span>
            <Select
              value={copyFrom}
              onValueChange={setCopyFrom}
              options={[{ value: 'none', label: 'Geen (leeg starten)' }, ...client.projects.map((p) => ({ value: p.id, label: p.name }))]}
            />
          </label>
        ) : null}
      </div>
    </Modal>
  )
}

function CategoryEditorModal({ client, projectId, category, onClose }: { client: ClientDetail; projectId: string; category?: CategoryView; onClose: () => void }) {
  const saveCategory = useSaveCategory(client.id)
  const deleteCategory = useDeleteCategory(client.id)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [name, setName] = useState(category?.name ?? '')
  const [sortOrder, setSortOrder] = useState(String(category?.sortOrder ?? client.categories.filter((c) => c.projectId === projectId).length + 1))
  const [active, setActive] = useState(category?.active ?? true)

  function save() {
    if (!name.trim()) return
    const input: CategoryInput = {
      name: name.trim(),
      projectId,
      sortOrder: Number(sortOrder) || 0,
      active,
    }
    saveCategory.mutate({ categoryId: category?.id, input }, { onSuccess: onClose })
  }

  if (confirmDelete && category) {
    return (
      <Modal
        open
        onOpenChange={(o) => !o && setConfirmDelete(false)}
        title="Categorie verwijderen"
        description="Dit kan alleen als er nog geen uren of factuurregels op deze categorie geboekt zijn. Zit er al iets op, zet hem dan op inactief."
        footer={
          <>
            <button type="button" className="btn-ghost" onClick={() => setConfirmDelete(false)}>Annuleren</button>
            <button
              type="button"
              className="btn-primary bg-status-overdue hover:bg-status-overdue/90"
              onClick={() => deleteCategory.mutate(category.id, { onSuccess: onClose })}
              disabled={deleteCategory.isPending}
            >
              <Trash2 className="h-4 w-4" />
              {deleteCategory.isPending ? 'Verwijderen…' : 'Verwijder'}
            </button>
          </>
        }
      >
        <p className="text-sm text-ink-soft">Weet je het zeker?</p>
      </Modal>
    )
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={category ? 'Categorie bewerken' : 'Nieuwe categorie'}
      footer={
        <>
          {category ? (
            <button type="button" className="btn-ghost mr-auto text-status-overdue" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4" /> Verwijderen
            </button>
          ) : null}
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={save} disabled={!name.trim() || saveCategory.isPending}>
            {saveCategory.isPending ? 'Opslaan…' : 'Opslaan'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Naam</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className="field" placeholder="Categorienaam" />
        </label>
        <div className="grid grid-cols-2 items-end gap-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Sortering</span>
            <input value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} inputMode="numeric" className="field num text-right" />
          </label>
          <label className="flex h-9 cursor-pointer items-center gap-1.5 text-sm text-ink-soft">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="h-3.5 w-3.5 accent-accent-600" />
            Actief
          </label>
        </div>
      </div>
    </Modal>
  )
}
